import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { tutorials, TutorialDefinition, TutorialStep } from '../data/tutorials';
import { creditsService } from '../services/CreditsService';
import { toastService } from '../components/ui/Toast';

export interface TutorialState {
  isActive: boolean;
  tutorialId: string | null;
  currentStepIndex: number;
  isPaused: boolean;
  completedTutorials: string[];
  pendingPromptFill: string | null;
}

export interface TutorialContextValue {
  // State
  state: TutorialState;
  currentTutorial: TutorialDefinition | null;
  currentStep: TutorialStep | null;
  totalSteps: number;
  progress: number; // 0-100
  
  // Actions
  startTutorial: (tutorialId: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipStep: () => void;
  exitTutorial: () => void;
  pauseTutorial: () => void;
  resumeTutorial: () => void;
  
  // Step validation
  completeAction: (actionType: string, payload?: { target?: string; nodeType?: string }) => void;

  // Prompt fill (for tutorial "Use" button)
  fillPrompt: (text: string) => void;
  clearPendingPromptFill: () => void;

  // Tutorial list
  availableTutorials: TutorialDefinition[];
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

const STORAGE_KEY = 'creative_nodeflow_completed_tutorials';

function loadCompletedTutorials(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveCompletedTutorials(completed: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Side effects that fire exactly once when a tutorial completes:
 *   - persist the completion to localStorage
 *   - claim the credit refund for any generation the tutorial drove
 *
 * Called from both `nextStep()` (when user clicks Next on the celebration
 * step) and `completeAction()` (when an action satisfies the final step).
 *
 * Idempotent in two layers: localStorage check here (so a second call in
 * the same session is a no-op) AND server-side dedup in /api/credits/
 * tutorial-refund (so even across sessions / browsers we never refund
 * twice). The server check is the source of truth.
 */
function handleTutorialCompleted(tutorialId: string): void {
  // Fire-and-forget refund. Best-effort: log on failure but never throw,
  // never block the celebration UI.
  creditsService
    .claimTutorialRefund(tutorialId)
    .then((result) => {
      if (result.alreadyRefunded) {
        return; // silent — they've seen this before
      }
      if (result.refunded > 0) {
        toastService.success(
          `Tutorial complete! You got ${result.refunded} credits back as a reward.`,
        );
      }
    })
    .catch((err) => {
      console.warn('[TutorialContext] tutorial refund failed:', err);
    });
}

export function TutorialProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TutorialState>({
    isActive: false,
    tutorialId: null,
    currentStepIndex: 0,
    isPaused: false,
    completedTutorials: loadCompletedTutorials(),
    pendingPromptFill: null,
  });
  
  const actionQueueRef = useRef<string[]>([]);
  const autoAdvanceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get current tutorial and step
  const currentTutorial = state.tutorialId 
    ? tutorials.find(t => t.id === state.tutorialId) || null 
    : null;
  
  const currentStep = currentTutorial 
    ? currentTutorial.steps[state.currentStepIndex] || null 
    : null;
  
  const totalSteps = currentTutorial?.steps.length || 0;
  const progress = totalSteps > 0 
    ? Math.round((state.currentStepIndex / totalSteps) * 100) 
    : 0;
  
  // Clear auto-advance timer on unmount or step change
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);
  
  // Handle auto-advance steps
  useEffect(() => {
    if (!currentStep || state.isPaused || !state.isActive) return undefined;
    
    if (currentStep.autoAdvance && currentStep.delay) {
      autoAdvanceTimerRef.current = setTimeout(() => {
        nextStep();
      }, currentStep.delay);
      
      return () => {
        if (autoAdvanceTimerRef.current) {
          clearTimeout(autoAdvanceTimerRef.current);
        }
      };
    }
    return undefined;
  }, [currentStep, state.isPaused, state.isActive, state.currentStepIndex]);
  
  const startTutorial = useCallback((tutorialId: string) => {
    const tutorial = tutorials.find(t => t.id === tutorialId);
    if (!tutorial) {
      console.error(`Tutorial not found: ${tutorialId}`);
      return;
    }
    
    setState(prev => ({
      ...prev,
      isActive: true,
      tutorialId,
      currentStepIndex: 0,
      isPaused: false,
    }));
    
    actionQueueRef.current = [];
  }, []);
  
  const nextStep = useCallback(() => {
    setState(prev => {
      if (!prev.isActive || !prev.tutorialId) return prev;
      
      const tutorial = tutorials.find(t => t.id === prev.tutorialId);
      if (!tutorial) return prev;
      
      const nextIndex = prev.currentStepIndex + 1;
      
      // Tutorial complete
      if (nextIndex >= tutorial.steps.length) {
        const wasAlreadyCompleted = prev.completedTutorials.includes(prev.tutorialId);
        const newCompleted = wasAlreadyCompleted
          ? prev.completedTutorials
          : [...prev.completedTutorials, prev.tutorialId];

        saveCompletedTutorials(newCompleted);
        if (!wasAlreadyCompleted) {
          handleTutorialCompleted(prev.tutorialId);
        }

        return {
          ...prev,
          isActive: false,
          tutorialId: null,
          currentStepIndex: 0,
          completedTutorials: newCompleted,
        };
      }

      return {
        ...prev,
        currentStepIndex: nextIndex,
      };
    });

    actionQueueRef.current = [];
  }, []);
  
  const prevStep = useCallback(() => {
    setState(prev => {
      if (!prev.isActive || prev.currentStepIndex <= 0) return prev;
      
      return {
        ...prev,
        currentStepIndex: prev.currentStepIndex - 1,
      };
    });
    
    actionQueueRef.current = [];
  }, []);
  
  const skipStep = useCallback(() => {
    nextStep();
  }, [nextStep]);
  
  const exitTutorial = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
    }
    
    setState(prev => ({
      ...prev,
      isActive: false,
      tutorialId: null,
      currentStepIndex: 0,
      isPaused: false,
    }));
    
    actionQueueRef.current = [];
  }, []);
  
  const pauseTutorial = useCallback(() => {
    setState(prev => ({ ...prev, isPaused: true }));
  }, []);
  
  const resumeTutorial = useCallback(() => {
    setState(prev => ({ ...prev, isPaused: false }));
  }, []);

  const fillPrompt = useCallback((text: string) => {
    setState(prev => ({ ...prev, pendingPromptFill: text }));
  }, []);

  const clearPendingPromptFill = useCallback(() => {
    setState(prev => ({ ...prev, pendingPromptFill: null }));
  }, []);
  
  // Called when user completes an action (e.g., adds a node, clicks a button)
  // Uses functional setState to always read the latest state — no stale closure risk.
  const completeAction = useCallback((actionType: string, payload?: { target?: string; nodeType?: string }) => {
    setState(prev => {
      if (!prev.isActive || !prev.tutorialId) return prev;

      const tutorial = tutorials.find(t => t.id === prev.tutorialId);
      if (!tutorial) return prev;

      const step = tutorial.steps[prev.currentStepIndex];
      if (!step?.action) return prev;

      // Check if this action matches what we're waiting for
      if (step.action.type !== actionType && step.action.type !== 'any') return prev;

      if (step.action.target) {
        if (!payload?.target || payload.target !== step.action.target) return prev;
      }
      if (step.action.nodeType) {
        if (!payload?.nodeType || payload.nodeType !== step.action.nodeType) return prev;
      }

      // Advance to next step
      const nextIndex = prev.currentStepIndex + 1;

      if (nextIndex >= tutorial.steps.length) {
        const wasAlreadyCompleted = prev.completedTutorials.includes(prev.tutorialId);
        const newCompleted = wasAlreadyCompleted
          ? prev.completedTutorials
          : [...prev.completedTutorials, prev.tutorialId];
        saveCompletedTutorials(newCompleted);
        if (!wasAlreadyCompleted) {
          handleTutorialCompleted(prev.tutorialId);
        }
        return { ...prev, isActive: false, tutorialId: null, currentStepIndex: 0, completedTutorials: newCompleted };
      }

      return { ...prev, currentStepIndex: nextIndex };
    });

    actionQueueRef.current = [];
  }, []); // No deps — functional setState always reads latest state
  
  const value: TutorialContextValue = {
    state,
    currentTutorial,
    currentStep,
    totalSteps,
    progress,
    startTutorial,
    nextStep,
    prevStep,
    skipStep,
    exitTutorial,
    pauseTutorial,
    resumeTutorial,
    completeAction,
    fillPrompt,
    clearPendingPromptFill,
    availableTutorials: tutorials,
  };
  
  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial(): TutorialContextValue {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}

export { TutorialContext };
